import React, { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { Button } from '@/components/Button'
import { Tabs, Tab, TabList, TabPanel } from '@/components/Tabs'

import EnergyEstimationEquationEditor from './EnergyEstimationEquationEditor'
import NutrientDistributionEditor from '@/features/settings/NutrientDistributionEditor'
import { MealDivisionEditor } from './MealDivisionEditor'
import ReadableStringUtils from '../utils/ReadableStringUtils'
import {
  getRecommenderSettings,
  resetRecommenderSettings,
  saveEnergyDistribution,
  saveRecommenderSettings,
} from '@/api/settings'
import {
  fetchEnabledDietaryRestrictions,
  getEnergyEstimationEquation,
  saveEnergyEstimationEquation,
  saveMealOfDayDivision,
  updateEnabledDietaryRestrictions,
} from '@/api/settings'
import RecommenderSettingsEditor from './RecommenderSettingsEditor'
import {
  EnergyDistribution,
  MacrosDistribution,
  Meal,
  MealOfDay,
  MealOfDayDivision,
  MealPart,
  NewMealOfDayDivision,
  RecommenderSettings,
} from '@/api/types'

const saveSettings = (
  energyDistribution: EnergyDistribution,
  mealOfDayDivisions: NewMealOfDayDivision[] | null,
  enabledDietaryRestrictions,
  energyEstimationEquation,
  recommenderSettings: RecommenderSettings,
) => {
  Promise.all([
    saveEnergyDistribution(energyDistribution),
    mealOfDayDivisions ? saveMealOfDayDivision(mealOfDayDivisions) : Promise.resolve(),
    updateEnabledDietaryRestrictions(enabledDietaryRestrictions),
    saveEnergyEstimationEquation(energyEstimationEquation),
    saveRecommenderSettings(recommenderSettings),
  ])
    .then((response) => {
      toast.success('Saved with success')
    })
    .catch((error) => {
      toast.error(error.message)
    })
}

export default function SettingsIndex({
  energyDistribution: energyDist,
  mealParts,
  recommenderSettings: recommenderSettingsProp,
  mealOfDayDivisions,
}: {
  energyDistribution: MacrosDistribution
  mealParts: MealPart[]
  recommenderSettings: RecommenderSettings
  mealOfDayDivisions: MealOfDayDivision[]
}) {
  const [tempMealOfDayDivision, setTempMealOfDayDivision] =
    useState<Array<MealOfDayDivision> | null>(null)

  const [enabledDietaryRestrictions, setEnabledDietaryRestrictions] = useState<Array<any>>([])
  const [energyDistribution, setEnergyDistribution] = useState(energyDist)
  const [energyEstimationEquation, setEnergyEstimationEquation] = useState<any>(null)
  const [showValidation, setShowValidation] = useState(false)
  const [isEnergyDistValid, setIsEnergyDistValid] = useState(true)

  const [recommenderSettings, setRecommenderSettings] =
    React.useState<RecommenderSettings>(recommenderSettingsProp)

  useEffect(() => {
    setTempMealOfDayDivision(mealOfDayDivisions)
  }, [mealOfDayDivisions])

  useEffect(() => {
    setEnergyDistribution(energyDistribution)
  }, [energyDistribution])

  useEffect(() => {
    setRecommenderSettings(recommenderSettingsProp)
  }, [recommenderSettingsProp])

  useEffect(() => {
    fetchEnabledDietaryRestrictions().then(setEnabledDietaryRestrictions)
    getEnergyEstimationEquation().then(setEnergyEstimationEquation)
  }, [])

  const updateNutrientDistribution = (nutrient: string, value: number) => {
    setEnergyDistribution((prev) => ({ ...prev, [nutrient]: value }))
  }

  const updateMealsDistribution = (updatedMeals: any, isEnergyDistValid: boolean) => {
    setEnergyDistribution(updatedMeals)
    setIsEnergyDistValid(isEnergyDistValid)
  }

  const saveAll = () => {
    if (validate()) {
      saveSettings(
        energyDistribution,
        tempMealOfDayDivision,
        enabledDietaryRestrictions,
        energyEstimationEquation,
        recommenderSettings,
      )
      setShowValidation(false)
    } else {
      setShowValidation(true)
    }
  }

  const validate = () => {
    const nutrientsTotal =
      energyDistribution.proteinDistribution +
      energyDistribution.fatDistribution +
      energyDistribution.carbohydratesDistribution

    return nutrientsTotal === 1 && isEnergyDistValid
  }

  const onChangeDietaryRestriction = (dietaryRestriction: any, check: boolean) => {
    setEnabledDietaryRestrictions((prev) =>
      check ? [...prev, dietaryRestriction] : prev.filter((d) => d !== dietaryRestriction),
    )
  }

  const updateEnergyEstimationEquation = (equation: any) => {
    setEnergyEstimationEquation(equation)
  }

  const resetRecommenderSettingsFunc = () => {
    resetRecommenderSettings()
      .then(() =>
        getRecommenderSettings().then((config) => {
          setRecommenderSettings(config)
          toast.success('Recommender settings successfully reset')
        }),
      )
      .catch(() => toast.error('Could not reset recommender settings'))
  }

  const onMealPreferencesChange = useCallback(
    (selectedMeals: Meal[], mealOfDayDivisions: MealOfDayDivision[]) =>
      setTempMealOfDayDivision([...mealOfDayDivisions]),
    [setTempMealOfDayDivision],
  )

  const dietaryRestrictions = ReadableStringUtils.dietaryRestrictions().map(
    (dietaryRestriction) => ({
      id: dietaryRestriction,
      name: ReadableStringUtils.readableDietaryRestriction(dietaryRestriction),
      isSelected: enabledDietaryRestrictions.includes(dietaryRestriction),
    }),
  )

  return (
    <div className="nutritionalRequirements">
      <div className="viewHeader row">
        <div className="viewHeader-controls">
          <Button variant="primary" onPress={saveAll}>
            Save
          </Button>
        </div>
      </div>

      <Tabs>
        <TabList aria-label="History of Ancient Rome">
          <Tab id="Kdn">Kcal distribution for nutrients</Tab>
          <Tab id="Eeee">Energy estimation equation editor</Tab>
          <Tab id="Mde">Meal division editor</Tab>
          <Tab id="RecommenderSettings">Recommender Settings</Tab>
        </TabList>
        <TabPanel id="Kdn">
          <NutrientDistributionEditor
            energyDistribution={energyDistribution}
            updateNutrientDistribution={updateNutrientDistribution}
            showValidation={showValidation}
          />
        </TabPanel>
        <TabPanel id="Eeee">
          <EnergyEstimationEquationEditor
            energyEstimationEquation={energyEstimationEquation}
            updateEnergyEstimationEquation={updateEnergyEstimationEquation}
          />
        </TabPanel>
        <TabPanel id="Mde">
          <MealDivisionEditor
            mealDivisions={tempMealOfDayDivision || mealOfDayDivisions}
            onChange={onMealPreferencesChange}
            generateMealDivisionId={() => null}
          />
        </TabPanel>
        {/* <TabPanel id="Edr">
            {dietaryRestrictions.map((dietaryRestriction) => (
              <Switch
                key={dietaryRestriction.id}
                isSelected={dietaryRestriction.isSelected}
                onChange={(value) =>
                  this.onChangeDietaryRestriction(dietaryRestriction.name, value)
                }>
                {dietaryRestriction.name}
              </Switch>
            ))}
            <div>
              <h4>
                Enabled restrictions will be available for users to select them. Need to ensure
                content availability. Try the meal simulation before enabling them.
              </h4>
            </div>
          </TabPanel> */}
        <TabPanel id="RecommenderSettings">
          {recommenderSettings && (
            <RecommenderSettingsEditor
              recommenderSettings={recommenderSettings}
              setRecommenderSettings={setRecommenderSettings}
              resetRecommenderSettings={resetRecommenderSettingsFunc}
            />
          )}
        </TabPanel>
      </Tabs>
    </div>
  )
}
